import { formatDocSubestipulante, formatVigenciaSubDisplay } from '../hooks/useHomeContent';
import type { DropdownOption } from '../types/componentType';
import type { ListaSubestipulanteItem } from '../types/vida/subEstipulante/subEstipulanteType';

const LEADING_DIGITS_RE = /^\d*/;

function FilterActiveSubestipulantes(
  itens: ListaSubestipulanteItem[] | null | undefined,
  filterActive: boolean,
): ListaSubestipulanteItem[] {
  if (itens == null || itens.length === 0) {
    return [];
  }
  if (!filterActive) {
    return [...itens];
  }
  return itens.filter((item) => {
    const cod = String(item.codStatusSubOut ?? '').trim().toUpperCase();
    if (cod === 'A') {
      return true;
    }
    const desc = String(item.dsCstatus ?? '').trim().toLowerCase();
    return desc === 'ativo';
  });
}

/** yyyy-mm-dd (or ISO with `T`) → local midnight; invalid → `null`. */
function parseDataFimVigenciaLocal(raw: string | undefined): Date | null {
  if (!raw?.trim()) return null;
  const head = raw.trim().split(/[Tt]/)[0]?.trim().split(/\s+/)[0] ?? '';
  const parts = head.split('-');
  if (parts.length < 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(LEADING_DIGITS_RE.exec(String(parts[2]))?.[0] ?? '', 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function inicioDoDiaLocal(ref: Date): Date {
  return new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
}

export type SubestipulanteVigenciaSituacao = 'Vencido' | 'Normal' | '';

/** Status displayed in label (API description or code). */
export function formatSubestipulanteStatusLabel(item: ListaSubestipulanteItem): string {
  const desc = String(item.dsCstatus ?? '').trim();
  if (desc) return desc;
  const cod = String(item.codStatusSubOut ?? '').trim();
  return cod || '—';
}

/** Vigency expired when the end date (only day, local) is before today. */
export function formatSubestipulanteVigenciaSituacao(
  item: ListaSubestipulanteItem,
  hoje: Date = new Date(),
): SubestipulanteVigenciaSituacao {
  const fim = parseDataFimVigenciaLocal(item.dataFimVigenciaOut);
  if (!fim) return '';
  const limite = inicioDoDiaLocal(hoje);
  return fim.getTime() < limite.getTime() ? 'Vencido' : 'Normal';
}

/** Keep only items with `dataFimVigenciaOut` (only day) >= today (only day, local timezone). */
function FilterEndOfValidityGreaterThanOrEqualToToday(
  lista: ListaSubestipulanteItem[],
  hoje: Date = new Date(),
): ListaSubestipulanteItem[] {
  const limite = inicioDoDiaLocal(hoje);
  return lista.filter((item) => {
    const fim = parseDataFimVigenciaLocal(item.dataFimVigenciaOut);
    if (!fim) return false;
    return fim.getTime() >= limite.getTime();
  });
}

function sortSubestipulantesList(lista: ListaSubestipulanteItem[]): ListaSubestipulanteItem[] {
  return [...lista].sort((a, b) => {
    const ca = a.codSubestipulanteOut ?? Number.MAX_SAFE_INTEGER;
    const cb = b.codSubestipulanteOut ?? Number.MAX_SAFE_INTEGER;
    if (ca !== cb) return ca - cb;
    return (a.nomeSubestipulanteOut ?? '').localeCompare(b.nomeSubestipulanteOut ?? '', 'pt-BR', {
      sensitivity: 'base',
    });
  });
}

/** Filtered and sorted subestipulantes for list/table views. */
export function getSortedSubestipulantesList(
  itens: ListaSubestipulanteItem[] | null | undefined,
  filterActiveSubestipulantes: boolean,
): ListaSubestipulanteItem[] {
  const base = FilterActiveSubestipulantes(itens, filterActiveSubestipulantes);
  const lista = filterActiveSubestipulantes
    ? FilterEndOfValidityGreaterThanOrEqualToToday(base)
    : base;

  return sortSubestipulantesList(lista);
}

/** Ex.: `1-ESTIPULANTE - 88540` */
export function formatSubestipulanteCodNome(
  cod?: number | string | null,
  nome?: string | null,
): string {
  const codTrimmed = cod != null ? String(cod).trim() : '';
  const nomeTrimmed = nome?.trim();
  if (codTrimmed && nomeTrimmed) {
    return `${codTrimmed}-${nomeTrimmed}`;
  }
  if (codTrimmed) {
    return codTrimmed;
  }
  if (nomeTrimmed) {
    return nomeTrimmed;
  }
  return '—';
}

export type SubestipulanteDropdownValueKey = 'identSubestipulanteOut' | 'codSubestipulanteOut';

/**
 * Options for `Dropdown`: sorting by `codSubestipulanteOut` and formatted labels.
 * @param filterActive if `true`, applies active filter and end of validity >= today; if `false`, does not filter by status or data (only normalizes empty list).
 * @param valueKey field used as option `value` (default: `identSubestipulanteOut`).
 */
export function subestipulanteDropdownOptions(
  itens: ListaSubestipulanteItem[] | null | undefined,
  filterActiveSubestipulantes: boolean,
  valueKey: SubestipulanteDropdownValueKey = 'identSubestipulanteOut',
): DropdownOption[] {
  const lista = getSortedSubestipulantesList(itens, filterActiveSubestipulantes);

  return lista
    .map((s) => {
      const nome = s.nomeSubestipulanteOut?.trim() || '—';
      const cod = s.codSubestipulanteOut != null ? String(s.codSubestipulanteOut) : '—';
      const doc = formatDocSubestipulante(s.numDocumentoOut);
      const ini = formatVigenciaSubDisplay(s.dataIniVigenciaOut);
      const fim = formatVigenciaSubDisplay(s.dataFimVigenciaOut);
      const status = formatSubestipulanteStatusLabel(s);
      const vigencia = formatSubestipulanteVigenciaSituacao(s);
      const value = valueKey === 'codSubestipulanteOut'
        ? cod
        : String(s.identSubestipulanteOut);
      return {
        value,
        label: `${cod} · ${nome} · ${doc} · ${ini} · ${fim} · ${status} · ${vigencia}`,
      };
    });
}
